import React, { useState } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { 
  CopyIcon, 
  DiffIcon,
  MoreHorizontalIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Tooltip } from "../ui/tooltip";
import { cn } from "../../lib/utils";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import { RequestViewButton } from "../shared/button/Buttons";
import {
  TestRunRequest,
  ToolCall,
} from "../../model_gen/evaluation";
import { getMessageContentText } from "../../utils/FormatUtils";

// Custom Score Tooltip Component
interface ScoreTooltipProps {
  children: React.ReactNode;
  score: number;
  results: Array<{
    criteria: string;
    score_value: number;
    description?: string;
  }>;
}

const ScoreTooltip: React.FC<ScoreTooltipProps> = ({ children, score, results }) => {
  const [isVisible, setIsVisible] = useState(false);

  const getScoreLabel = (score: number) => {
    if (score >= 90) return { label: "EXCELLENT", color: "#52c41a" };
    if (score >= 75) return { label: "GREAT", color: "#b7eb8f" };
    if (score >= 60) return { label: "GOOD", color: "#d9d9d9" };
    if (score >= 40) return { label: "FAIR", color: "#ffa39e" };
    return { label: "POOR", color: "#f5222d" };
  };

  const scoreLabel = getScoreLabel(score);
  const sortedResults = [...results].sort((a, b) => a.criteria.localeCompare(b.criteria));

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-[9999] pointer-events-none">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 w-96 max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: scoreLabel.color }}
                />
                <span className="font-semibold text-foreground text-sm">
                  Overall Score
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xl font-bold text-foreground">{score}%</span>
                <span 
                  className="text-xs font-medium px-2 py-1 rounded-full"
                  style={{ 
                    backgroundColor: `${scoreLabel.color}20`,
                    color: scoreLabel.color 
                  }}
                >
                  {scoreLabel.label}
                </span>
              </div>
            </div>

            {/* Criteria Breakdown */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                CRITERIA BREAKDOWN
              </div>
              {sortedResults.map((result, index) => (
                <div key={index} className="space-y-1">
                  {/* Use a grid layout to ensure proper spacing */}
                  <div className="grid grid-cols-[1fr,auto] gap-3 items-start">
                    <div className="min-w-0"> {/* min-w-0 allows text to shrink */}
                      <span className="text-sm font-medium text-foreground break-words">
                        {result.criteria}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Star rating visual - flex-shrink-0 prevents compression */}
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <div
                            key={star}
                            className={cn(
                              "w-3 h-3 rounded-sm flex-shrink-0",
                              star <= result.score_value
                                ? "bg-maitai-green"
                                : "bg-gray-200 dark:bg-gray-600"
                            )}
                          />
                        ))}
                      </div>
                      <span className="text-xs font-mono text-muted-foreground ml-1 whitespace-nowrap">
                        {result.score_value}/5
                      </span>
                    </div>
                  </div>
                  {result.description && (
                    <p className="text-xs text-muted-foreground leading-relaxed pl-1 break-words">
                      {result.description}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Tooltip arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white dark:border-t-gray-800"></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Custom Responses Tooltip Component  
interface ResponsesTooltipProps {
  children: React.ReactNode;
  record: TestRunRequest;
}

const ResponsesTooltip: React.FC<ResponsesTooltipProps> = ({ children, record }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ left: "50%", transform: "translateX(-50%)" });
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const renderToolCalls = (toolCalls: ToolCall[]) => {
    return toolCalls.map((toolCall: ToolCall) => (
      <div key={toolCall.id} className="text-xs text-muted-foreground mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border-l-2 border-maitai-green">
        <div className="font-medium text-foreground">{toolCall.function.name}</div>
        <div className="font-mono text-xs">{toolCall.function.arguments}</div>
      </div>
    ));
  };

  if (!record.original_request) {
    return (
      <Tooltip content="No request found">
        {children}
      </Tooltip>
    );
  }

  const originalResponse =
    getMessageContentText(
      record.original_request.metadata?._maitai_test_request_response as
        | string
        | { [k: string]: unknown }[]
        | null
        | undefined
    ) ||
    getMessageContentText(
      record.original_request?.params?.messages?.[
        record.original_request?.params?.messages?.length - 1
      ]?.content
    ) ||
    "No response";

  const testResponse =
    getMessageContentText(record.response?.choices?.[0]?.message?.content) ||
    "No response";

  const originalToolCalls = 
    (record.original_request.metadata?._maitai_test_request_response_tool_calls as ToolCall[]) ||
    (record.original_request?.params?.messages?.[
      record.original_request?.params?.messages?.length - 1
    ]?.tool_calls as ToolCall[]) || [];

  const testToolCalls = 
    (record.response?.choices?.[0]?.message?.tool_calls as ToolCall[]) || [];

  const updatePosition = () => {
    if (!containerRef.current || !tooltipRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipWidth = 900; // Our tooltip width
    const viewportWidth = window.innerWidth;
    const margin = 16; // Safety margin from screen edges

    // Calculate the ideal center position
    const idealLeft = containerRect.left + containerRect.width / 2;
    
    // Calculate how much space we need on each side
    const halfTooltipWidth = tooltipWidth / 2;
    
    // Check if tooltip would overflow on the left
    if (idealLeft - halfTooltipWidth < margin) {
      // Position tooltip flush with left margin
      const leftOffset = margin - (idealLeft - halfTooltipWidth);
      setPosition({
        left: "50%",
        transform: `translateX(calc(-50% + ${leftOffset}px))`
      });
    }
    // Check if tooltip would overflow on the right
    else if (idealLeft + halfTooltipWidth > viewportWidth - margin) {
      // Position tooltip flush with right margin
      const rightOffset = (idealLeft + halfTooltipWidth) - (viewportWidth - margin);
      setPosition({
        left: "50%",
        transform: `translateX(calc(-50% - ${rightOffset}px))`
      });
    }
    // Tooltip fits perfectly centered
    else {
      setPosition({
        left: "50%",
        transform: "translateX(-50%)"
      });
    }
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
    // Update position after the tooltip is visible
    setTimeout(updatePosition, 0);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // Update position on window resize
  React.useEffect(() => {
    if (!isVisible) return;

    const handleResize = () => {
      updatePosition();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isVisible]);

  return (
    <div 
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div 
          className="absolute bottom-full mb-2 z-[9999] pointer-events-none"
          style={{
            left: position.left,
            transform: position.transform
          }}
        >
          <div 
            ref={tooltipRef}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-6 w-[900px] max-w-[900px]"
          >
            {/* Header */}
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
              <DiffIcon className="w-4 h-4 text-maitai-green" />
              <span className="font-semibold text-foreground">Response Comparison</span>
            </div>

            {/* Two-column comparison */}
            <div className="grid grid-cols-2 gap-6">
              {/* Original Response */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="font-medium text-foreground text-sm">
                    Original Response
                    {record.original_request.metadata?._maitai_test_request_response && (
                      <span className="text-maitai-green ml-1">*</span>
                    )}
                  </span>
                </div>
                <div className="max-h-[300px] overflow-y-auto bg-gray-50 dark:bg-gray-900 rounded border p-3">
                  <div className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
                    {originalResponse}
                  </div>
                  {originalToolCalls.length > 0 && (
                    <div className="mt-3">
                      <div className="font-medium text-foreground text-xs mb-2">Tool Calls:</div>
                      {renderToolCalls(originalToolCalls)}
                    </div>
                  )}
                </div>
              </div>

              {/* Test Response */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-maitai-green rounded-full"></div>
                  <span className="font-medium text-foreground text-sm">Test Response</span>
                </div>
                <div className="max-h-[300px] overflow-y-auto bg-gray-50 dark:bg-gray-900 rounded border p-3">
                  <div className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
                    {testResponse}
                  </div>
                  {testToolCalls.length > 0 && (
                    <div className="mt-3">
                      <div className="font-medium text-foreground text-xs mb-2">Tool Calls:</div>
                      {renderToolCalls(testToolCalls)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Legend note */}
            {record.original_request.metadata?._maitai_test_request_response && (
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="text-xs text-muted-foreground">
                  <span className="text-maitai-green">*</span> Indicates edited/corrected response from test set
                </div>
              </div>
            )}

            {/* Tooltip arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white dark:border-t-gray-800"></div>
          </div>
        </div>
      )}
    </div>
  );
};

interface TestRunRequestsTableProps {
  requests: TestRunRequest[];
  loading?: boolean;
  apiKey: ApiKeyOrGetter;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onRowClick?: (record: TestRunRequest) => void;
  onCompareClick?: (requestId: number) => void;
  testSetId?: string;
  systemPrompt?: string;
  className?: string;
  selectedScores: number[];
  onScoreFilterClear: () => void;
}

const TestRunRequestsTable: React.FC<TestRunRequestsTableProps> = ({
  requests,
  loading = false,
  apiKey,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onRowClick,
  onCompareClick,
  testSetId,
  systemPrompt,
  className,
  selectedScores,
  onScoreFilterClear,
}) => {
  const [expandedTooltips, setExpandedTooltips] = useState<Set<string>>(new Set());



  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here if available in your project
  };



  const renderRequestId = (requestId: string) => {
    const shortRequestId = requestId.length > 8 ? `...${requestId.slice(-8)}` : requestId;
    return (
      <div className="flex items-center gap-2">
        <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
          {shortRequestId}
        </code>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(requestId);
          }}
        >
          <CopyIcon className="w-3 h-3" />
        </Button>
      </div>
    );
  };

  const renderStatusBadge = (status: string) => {
    const statusConfig = {
      CREATED: {
        variant: "secondary" as const,
        className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200"
      },
      PENDING: {
        variant: "outline" as const,
        className: "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200"
      },
      COMPLETED: {
        variant: "default" as const,
        className: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200"
      },
      ERROR: {
        variant: "destructive" as const,
        className: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200"
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.CREATED;

    return (
      <span 
        className={cn(
          "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
          // No transitions or hover effects for status badges
          config.className
        )}
      >
        {status}
      </span>
    );
  };

  const renderTags = (record: TestRunRequest) => {
    try {
      const metaData = JSON.parse(
        (record.original_request?.metadata?._maitai_test_request_meta as string) || "{}"
      );
      const tags = metaData.tags || [];
      
      if (tags.length === 0) {
        return <span className="text-sm text-muted-foreground">No tags</span>;
      }

      const maxTagsToShow = 3;
      const isExpanded = expandedTooltips.has(record.original_request_id || "");
      const visibleTags = isExpanded ? tags : tags.slice(0, maxTagsToShow);
      const hasMoreTags = tags.length > maxTagsToShow;

      return (
        <div className="flex flex-wrap gap-1 items-center">
          {visibleTags.map((tag: string, index: number) => (
            <Badge 
              key={index} 
              variant="outline" 
              className="text-xs border-maitai-green text-maitai-green bg-maitai-green/5 hover:bg-maitai-green/10 font-medium"
            >
              {tag}
            </Badge>
          ))}
          {hasMoreTags && !isExpanded && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                const newExpanded = new Set(expandedTooltips);
                newExpanded.add(record.original_request_id || "");
                setExpandedTooltips(newExpanded);
              }}
            >
              <ChevronDownIcon className="w-3 h-3 mr-1" />
              +{tags.length - maxTagsToShow} more
            </Button>
          )}
          {isExpanded && hasMoreTags && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                const newExpanded = new Set(expandedTooltips);
                newExpanded.delete(record.original_request_id || "");
                setExpandedTooltips(newExpanded);
              }}
            >
              <ChevronUpIcon className="w-3 h-3 mr-1" />
              Show less
            </Button>
          )}
        </div>
      );
    } catch (e) {
      console.error("Error parsing metadata:", e);
      return <span className="text-sm text-muted-foreground">No tags</span>;
    }
  };

  const renderScore = (record: TestRunRequest) => {
    const results = record.qualitative_evaluation_results || [];
    if (results.length === 0) return <span className="text-sm text-muted-foreground">-</span>;

    const maxPossibleScore = results.length * 5; // Each result can score max 5
    const totalScore = results.reduce(
      (sum, result) => sum + (result.score_value || 0),
      0
    );
    const normalizedScore = Math.round((totalScore / maxPossibleScore) * 100);

    const getScoreColor = (score: number) => {
      // Using the same color system as TestRunDetail.tsx score distribution
      if (score >= 90) return {
        bg: "bg-[#52c41a]", // PERFECT - green
        text: "text-white",
        border: "border-[#52c41a]"
      };
      if (score >= 75) return {
        bg: "bg-[#b7eb8f]", // GREAT - light green  
        text: "text-green-800",
        border: "border-[#b7eb8f]"
      };
      if (score >= 60) return {
        bg: "bg-[#d9d9d9]", // GOOD - gray
        text: "text-gray-800", 
        border: "border-[#d9d9d9]"
      };
      if (score >= 40) return {
        bg: "bg-[#ffa39e]", // FAIR - light red
        text: "text-red-800",
        border: "border-[#ffa39e]"
      };
      return {
        bg: "bg-[#f5222d]", // POOR - red
        text: "text-white",
        border: "border-[#f5222d]"
      };
    };

    const sortedResults = [...results].sort((a, b) => a.criteria.localeCompare(b.criteria));
    const scoreColors = getScoreColor(normalizedScore);

    return (
      <ScoreTooltip score={normalizedScore} results={sortedResults}>
        <span 
          className={cn(
            "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium cursor-help",
            scoreColors.bg,
            scoreColors.text,
            scoreColors.border
          )}
        >
          {normalizedScore}%
        </span>
      </ScoreTooltip>
    );
  };

  const renderResponseTime = (record: TestRunRequest) => {
    if (!record.response?.response_time) {
      return <span className="text-sm text-muted-foreground">-</span>;
    }

    return (
      <Tooltip content={`Response time: ${record.response.response_time}ms`}>
        <span className="text-sm font-gc-reg text-foreground">
          {record.response.response_time}ms
        </span>
      </Tooltip>
    );
  };



  const renderActions = (record: TestRunRequest) => {
    return (
      <div className="flex items-center gap-2">
        <RequestViewButton
          apiKey={apiKey}
          request={record.original_request}
          to={`/application/${record.original_request?.application_id}/session/${encodeURIComponent(
            record.original_request?.session_id || ""
          )}/request/${record.original_request?.request_id}`}
          systemPrompt={systemPrompt}
          buttonStyle={{ color: "#21b892" }}
          testSetId={testSetId}
          testSetRequestId={record.original_request?.metadata?._maitai_test_request_id as string}
        >
          View
        </RequestViewButton>

        <ResponsesTooltip record={record}>
          <Button variant="ghost" size="sm" className="gap-1">
            <DiffIcon className="w-3 h-3" />
            Responses
          </Button>
        </ResponsesTooltip>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="w-8 h-8 p-0">
              <MoreHorizontalIcon className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (onCompareClick && record.id) {
                  onCompareClick(record.id);
                }
              }}
            >
              Compare
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const columns: PaginatedTableColumn<TestRunRequest>[] = [
    {
      key: "original_request_id",
      header: "Request ID",
      width: "w-[15%]",
      render: (record) => renderRequestId(record.original_request_id || ""),
    },
    {
      key: "status",
      header: "Status",
      width: "w-[15%]",
      render: (record) => renderStatusBadge(record.status),
    },
    {
      key: "tags" as keyof TestRunRequest,
      header: "Tags",
      width: "w-[20%]",
      render: (record) => renderTags(record),
    },
    {
      key: "score" as keyof TestRunRequest,
      header: "Score",
      width: "w-[15%]",
      render: (record) => renderScore(record),
    },
    {
      key: "response_time" as keyof TestRunRequest,
      header: "Time",
      width: "w-[10%]",
      render: (record) => renderResponseTime(record),
    },
    {
      key: "actions" as keyof TestRunRequest,
      header: "Actions",
      width: "w-[25%]",
      render: (record) => renderActions(record),
    },
  ];

  const showCustomPagination = totalPages > 1;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Score Filter Display */}
      {selectedScores.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm font-gc-med text-muted-foreground">
            Filtered by scores
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {selectedScores.map((score) => {
                const scoreLabels: Record<number, string> = {
                  1: "POOR (-2)",
                  2: "FAIR (-1)",
                  3: "GOOD (0)",
                  4: "GREAT (+1)",
                  5: "PERFECT (+2)",
                };
                const scoreColorConfig: Record<number, { variant: any; className: string }> = {
                  1: { 
                    variant: "destructive", 
                    className: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200" 
                  },
                  2: { 
                    variant: "outline", 
                    className: "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900 dark:text-orange-200" 
                  },
                  3: { 
                    variant: "secondary", 
                    className: "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-200" 
                  },
                  4: { 
                    variant: "outline", 
                    className: "bg-maitai-green/10 text-maitai-green border-maitai-green/30" 
                  },
                  5: { 
                    variant: "default", 
                    className: "bg-maitai-green text-white border-maitai-green" 
                  },
                };
                const config = scoreColorConfig[score];
                return (
                  <Badge
                    key={score}
                    variant={config.variant}
                    className={cn("text-xs font-medium", config.className)}
                  >
                    {scoreLabels[score]}
                  </Badge>
                );
              })}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onScoreFilterClear}
              className="text-xs"
            >
              Clear All
            </Button>
          </div>
        </div>
      )}

      <PaginatedTable
        data={requests}
        columns={columns}
        loading={loading}
        currentPage={currentPage}
        onPageChange={onPageChange}
        pageSize={itemsPerPage}
        totalItems={totalItems}
        emptyTitle="No test run requests found"
        emptyDescription="There are no requests for this test run."
        onRowClick={onRowClick}
        showPagination={showCustomPagination}
        loadingRows={5}
        serverSidePagination={true}
      />
    </div>
  );
};

export default TestRunRequestsTable;
